#
<template>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css" />
  <div class="landing-page" :class="{ 'reduce-motion': prefersReducedMotion }">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <div class="scroll-progress" aria-hidden="true">
      <div class="bar" :style="{ width: progress + '%' }"></div>
    </div>

    <main class="layout">
      <div ref="heroAnchor">
        <HeroPortal :reduce-motion="prefersReducedMotion" @cta="scrollToOferta" />
      </div>
      <Oferta2 @cta="(planKey) => openCheckoutModal('oferta2-section', planKey)" />
      <SobreZima />
      <ParaQuem />
      <Conteudo />
      <ComoFunciona />
      <Prova />
      <Oferta @cta="(planKey) => openCheckoutModal('oferta-section', planKey)" />
      <Manifesto />
      <CtaFinal @cta="() => openCheckoutModal('cta-final', 'mensal')" />
    </main>

    <footer class="landing-footer">
      <p class="disclaimer">
        Criptoativos são voláteis. Nada aqui garante lucro. Você é responsável pelas suas decisões.
      </p>
      <p class="guarantee">Garantia de até 7 dias para reembolso.</p>
    </footer>

    <div class="sticky-cta" v-show="showStickyCta">
      <button class="cta primary" @click="() => openCheckoutModal('sticky-cta', 'mensal')">[ Come&ccedil;ar agora &mdash; R$97/m&ecirc;s ]</button>
    </div>

    <teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="isCheckoutOpen"
          class="checkout-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-title"
          @click.self="closeCheckoutModal"
        >
          <div class="checkout-modal" data-cy="checkout-modal">
            <button class="modal-close" type="button" aria-label="Fechar modal" @click="closeCheckoutModal">
              <span aria-hidden="true">&times;</span>
            </button>

            <div class="checkout-head">
              <p class="kicker">{{ selectedCheckoutPlan.checkoutKicker }}</p>
              <h2 id="checkout-title">Finalize seu cadastro e pagamento</h2>
            </div>

            <div class="checkout-grid">
              <form class="checkout-form" @submit.prevent="submitCheckout" novalidate data-cy="checkout-form">
                <label>
                  <span>CPF/CNPJ</span>
                  <input
                    v-model.trim="form.cpf_cnpj"
                    type="text"
                    inputmode="numeric"
                    autocomplete="off"
                    placeholder="Digite seu CPF ou CNPJ"
                    maxlength="14"
                    @input="sanitizeCpfCnpjInput"
                    data-cy="signup-cpf"
                    required
                  />
                </label>

                <label>
                  <span>Email</span>
                  <input
                    v-model.trim="form.email"
                    type="email"
                    autocomplete="email"
                    placeholder="seu@email.com"
                    data-cy="signup-email"
                    required
                  />
                </label>

                <label>
                  <span>Usuário</span>
                  <input
                    v-model.trim="form.user_name"
                    type="text"
                    autocomplete="username"
                    placeholder="Usuário"
                    maxlength="16"
                    @input="sanitizeUserNameInput"
                    data-cy="signup-user"
                    required
                  />
                </label>

                <label class="password-field">
                  <span>Senha</span>
                  <input
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    minlength="8"
                    placeholder="Mínimo 8 caracteres"
                    data-cy="signup-password"
                    required
                  />
                  <button
                    type="button"
                    class="password-toggle"
                    @click="showPassword = !showPassword"
                    :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                  >
                    <i :class="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                  </button>
                </label>

                <label>
                  <span>Forma de pagamento</span>
                  <div class="payment-tabs" role="tablist" aria-label="Forma de pagamento">
                    <button
                      type="button"
                      class="payment-tab"
                      :class="{ active: form.billing_type === 'PIX' }"
                      @click="form.billing_type = 'PIX'"
                      data-cy="billing-pix"
                    >
                      PIX
                    </button>
                    <button
                      type="button"
                      class="payment-tab"
                      :class="{ active: form.billing_type === 'CREDIT_CARD' }"
                      @click="form.billing_type = 'CREDIT_CARD'"
                    >
                      CARTÃO
                    </button>
                    <button
                      type="button"
                      class="payment-tab"
                      :class="{ active: form.billing_type === 'BOLETO' }"
                      @click="form.billing_type = 'BOLETO'"
                    >
                      BOLETO
                    </button>
                  </div>
                </label>

                <template v-if="isCreditCardSelected">
                  <div class="credit-fields">
                  <label>
                    <span>Parcelas</span>
                    <div class="installments-grid" role="group" aria-label="Escolha o número de parcelas">
                      <button
                        v-for="n in 12"
                        :key="`inst-${n}`"
                        type="button"
                        class="installment-btn"
                        :class="{ active: form.installment_count === n }"
                        @click="form.installment_count = n"
                      >
                        {{ n }}x
                      </button>
                    </div>
                  </label>

                  <label>
                    <span>Nome no cartão</span>
                    <input v-model.trim="form.credit_card.holder_info.name" type="text" autocomplete="cc-name" required />
                  </label>

                  <label>
                    <span>Número do cartão</span>
                    <input
                      v-model.trim="form.credit_card.number"
                      type="text"
                      inputmode="numeric"
                      autocomplete="cc-number"
                      maxlength="19"
                      @input="sanitizeCardNumberInput"
                      required
                    />
                  </label>

                  <div class="card-row">
                    <label>
                      <span>Validade</span>
                      <input
                        v-model.trim="form.credit_card.expiry"
                        type="text"
                        inputmode="numeric"
                        placeholder="MM/AAAA"
                        maxlength="7"
                        @input="sanitizeExpiryInput"
                        required
                      />
                    </label>
                    <label>
                      <span>CVV</span>
                      <input
                        v-model.trim="form.credit_card.ccv"
                        type="password"
                        inputmode="numeric"
                        autocomplete="cc-csc"
                        placeholder="123"
                        maxlength="3"
                        @input="sanitizeCvvInput"
                        required
                      />
                    </label>
                  </div>

                  <div class="card-row">
                    <label>
                      <span>CEP</span>
                      <input
                        v-model.trim="form.credit_card.holder_info.postal_code"
                        type="text"
                        inputmode="numeric"
                        maxlength="8"
                        @input="sanitizeCepInput"
                        required
                      />
                    </label>
                    <label>
                      <span>Número</span>
                      <input
                        v-model.trim="form.credit_card.holder_info.address_number"
                        type="text"
                        inputmode="numeric"
                        @input="sanitizeAddressNumberInput"
                        required
                      />
                    </label>
                  </div>

                  <label>
                    <span>Telefone</span>
                    <input
                      v-model.trim="form.credit_card.holder_info.phone"
                      type="text"
                      inputmode="numeric"
                      placeholder="11999999999"
                      maxlength="11"
                      @input="sanitizePhoneInput"
                      required
                    />
                  </label>
                  </div>
                </template>

                <p v-if="formError" class="form-error">{{ formError }}</p>
                <p v-if="checkoutSuccessMessage" class="form-success">{{ checkoutSuccessMessage }}</p>

                <div class="actions">
                  <button class="cta primary full" type="submit" :disabled="isSubmitting" data-cy="submit-signup">
                    {{ isSubmitting ? 'Processando...' : 'Criar cobrança e conta' }}
                  </button>
                </div>
              </form>

                <aside class="checkout-side">
                  <div class="summary-card">
                    <p class="summary-label">Plano</p>
                    <h3>{{ selectedCheckoutPlan.checkoutName }}</h3>
                    <p class="summary-wow-badge">{{ checkoutDiscountBadge }}</p>
                    <p v-if="showFullPriceLabel" class="summary-price-old">{{ fullPriceLabel }}</p>
                    <p class="summary-price">{{ displayedCheckoutPrice }}</p>
                    <p class="summary-meta summary-discount">
                      {{ paymentSummaryText }}
                    </p>
                    <p class="summary-saving">{{ checkoutSavingsLabel }}</p>
                  </div>

                <div v-if="payment" class="payment-card payment-cta-card">
                  <p class="summary-label">Pagamento</p>
                  <p class="summary-meta">Sua cobranca foi gerada. Abra o pop-up para concluir o pagamento.</p>
                  <button class="ghost" type="button" @click="openPaymentPopup">
                    Abrir pop-up de pagamento
                  </button>
                </div>

                <div v-if="checkoutSuccessVisible" class="payment-card success-preview-card">
                  <p class="success-preview-title">Pagamento confirmado</p>
                  <p class="summary-meta">Cadastro finalizado com sucesso.</p>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="isPaymentPopupOpen && payment"
          class="payment-popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-popup-title"
          @click.self="closePaymentPopup"
        >
          <div class="payment-popup" data-cy="payment-popup">
            <button class="modal-close" type="button" aria-label="Fechar pagamento" @click="closePaymentPopup">
              <span aria-hidden="true">&times;</span>
            </button>
            <h3 id="payment-popup-title">Finalize seu pagamento</h3>

            <div v-if="shouldShowPixQr" class="pix-qr-box" data-cy="pix-qr-box">
              <p class="pix-label">QR Code PIX</p>
              <img class="pix-qr-image" :src="pixQrCodeSrc" alt="QR Code para pagamento via PIX" data-cy="pix-qr-image" />
            </div>

            <div v-if="shouldShowPixCopyPaste" class="pix-box" data-cy="pix-copy-box">
              <p class="pix-label">PIX copia e cola</p>
              <code>{{ payment.pix_copy_paste }}</code>
              <button class="ghost mini" type="button" @click="copyPix">
                {{ pixCopied ? 'Copiado' : 'Copiar codigo PIX' }}
              </button>
            </div>

            <div class="payment-links">
              <a
                v-if="payment.invoice_url"
                :href="payment.invoice_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir fatura
              </a>
              <a
                v-if="payment.bank_slip_url"
                :href="payment.bank_slip_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir boleto
              </a>
            </div>

            <p class="summary-meta">Expira em: {{ formatDate(payment.expires_at) }}</p>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
/* global defineOptions, process */
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { API_BASE_URL } from '@/config/env';
import { useScrollProgress } from '@/composables/useScrollProgress';
import HeroPortal from './landing/HeroPortal.vue';
import SobreZima from './landing/SobreZima.vue';
import ParaQuem from './landing/ParaQuem.vue';
import Conteudo from './landing/Conteudo.vue';
import ComoFunciona from './landing/ComoFunciona.vue';
import Prova from './landing/Prova.vue';
import Oferta from './landing/Oferta.vue';
import Oferta2 from './landing/Oferta2.vue';
import Manifesto from './landing/Manifesto.vue';
import CtaFinal from './landing/CtaFinal.vue';

defineOptions({ name: 'LandingPage' });

const { progress, prefersReducedMotion } = useScrollProgress();
const router = useRouter();
const heroAnchor = ref(null);
const showStickyCta = ref(false);
const isCheckoutOpen = ref(false);
const isPaymentPopupOpen = ref(false);
const isSubmitting = ref(false);
const showPassword = ref(false);
const formError = ref('');
const checkoutSuccessMessage = ref('');
const checkoutSuccessVisible = ref(false);
const payment = ref(null);
const liveStatus = ref('');
const pixCopied = ref(false);
const lastSubmittedBillingType = ref('');

let heroObserver = null;
let revealObserver = null;
let paymentPollTimer = null;
let loginRedirectTimer = null;

const PAID_STATUSES = ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'];
const CHECKOUT_LOG_PREFIX = '[checkout-flow]';
const CHECKOUT_DEBUG =
  process.env.NODE_ENV !== 'production'
  || String(window?.location?.search || '').includes('checkoutDebug=1');

const maskEmail = (email) => {
  const value = String(email || '').trim();
  if (!value) return '';
  const parts = value.split('@');
  if (parts.length !== 2) return '***';
  const local = parts[0];
  const domain = parts[1];
  if (local.length <= 1) return `***@${domain}`;
  if (local.length === 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local.slice(-1)}@${domain}`;
};

const logCheckout = (level, event, data = {}) => {
  if (!CHECKOUT_DEBUG) return;
  const logger = console[level] || console.log;
  logger(`${CHECKOUT_LOG_PREFIX} ${event}`, {
    ts: new Date().toISOString(),
    ...data,
  });
};

const form = ref({
  cpf_cnpj: '',
  email: '',
  user_name: '',
  password: '',
  billing_type: 'PIX',
  plan_id: 1,
  installment_count: 1,
  credit_card: {
    holder_name: '',
    number: '',
    expiry: '',
    ccv: '',
    holder_info: {
      name: '',
      email: '',
      cpf_cnpj: '',
      postal_code: '',
      address_number: '',
      phone: '',
    },
  },
});

const CHECKOUT_PLANS = {
  mensal: {
    planId: 1,
    checkoutName: 'Zima Mensal',
    checkoutKicker: 'Acesso mensal',
    fullPrice: 97,
    pixPrice: 92.15,
    cardOneTimePrice: 97,
  },
  trimestral: {
    planId: 1,
    checkoutName: 'Zima Trimestral',
    checkoutKicker: 'Acesso trimestral',
    fullPrice: 247,
    pixPrice: 234.65,
    cardOneTimePrice: 247,
  },
  semestral: {
    planId: 1,
    checkoutName: 'Zima Semestral',
    checkoutKicker: 'Acesso semestral',
    fullPrice: 447,
    pixPrice: 353.88,
    cardOneTimePrice: 372.5,
  },
  anual: {
    planId: 1,
    checkoutName: 'Zima Anual',
    checkoutKicker: 'Acesso anual',
    fullPrice: 797,
    pixPrice: 630.8,
    cardOneTimePrice: 664.18,
  },
};
const USERNAME_MAX_LENGTH = 16;
const selectedCheckoutPlanKey = ref('mensal');
const isPixSelected = computed(() => form.value.billing_type === 'PIX');
const isCreditCardSelected = computed(() => form.value.billing_type === 'CREDIT_CARD');
const selectedCheckoutPlan = computed(() => CHECKOUT_PLANS[selectedCheckoutPlanKey.value] || CHECKOUT_PLANS.mensal);
const isCardOneTimeSelected = computed(() => isCreditCardSelected.value && Number(form.value.installment_count || 1) === 1);
const checkoutReferenceValue = computed(() => Number(selectedCheckoutPlan.value.fullPrice || 0));
const checkoutDisplayValue = computed(() => {
  const plan = selectedCheckoutPlan.value;
  if (isPixSelected.value) return Number(plan.pixPrice || plan.fullPrice || 0);
  if (isCardOneTimeSelected.value) return Number(plan.cardOneTimePrice || plan.fullPrice || 0);
  return Number(plan.fullPrice || 0);
});
const displayedCheckoutPrice = computed(() => `R$ ${toBRL(checkoutDisplayValue.value)}`);
const paymentBillingType = computed(() => String(payment.value?.billing_type || lastSubmittedBillingType.value || '').toUpperCase());
const isPixPayment = computed(() => paymentBillingType.value === 'PIX');
const pixQrCodeSrc = computed(() => {
  const raw = String(payment.value?.pix_qr_code || '').trim();
  if (!raw) return '';
  if (raw.startsWith('data:image/')) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  return `data:image/png;base64,${raw.replace(/\s/g, '')}`;
});
const shouldShowPixQr = computed(() => isPixPayment.value && Boolean(pixQrCodeSrc.value));
const shouldShowPixCopyPaste = computed(() => isPixPayment.value && Boolean(payment.value?.pix_copy_paste));
const fullPriceLabel = computed(() => `R$ ${toBRL(checkoutReferenceValue.value)}`);
const showFullPriceLabel = computed(() => checkoutDisplayValue.value < checkoutReferenceValue.value);
const checkoutDiscountRate = computed(() => {
  if (!checkoutReferenceValue.value) return 0;
  return Number(((checkoutReferenceValue.value - checkoutDisplayValue.value) / checkoutReferenceValue.value).toFixed(6));
});
const checkoutDiscountBadge = computed(() => {
  if (isPixSelected.value) {
    if (selectedCheckoutPlan.value.cardOneTimePrice < selectedCheckoutPlan.value.fullPrice) return 'PIX com 5% extra sobre o valor à vista';
    return '5% OFF no PIX';
  }
  if (isCardOneTimeSelected.value && checkoutDisplayValue.value < checkoutReferenceValue.value) return 'Desconto à vista no cartão';
  if (isCardOneTimeSelected.value) return 'Cartão à vista';
  if (form.value.billing_type === 'BOLETO') return 'Valor cheio no boleto';
  return 'Parcelado no cartão';
});
const checkoutSavingsValue = computed(() => Number((checkoutReferenceValue.value - checkoutDisplayValue.value).toFixed(2)));
const checkoutSavingsLabel = computed(() => {
  if (checkoutSavingsValue.value <= 0) return 'Acesso imediato após confirmação do pagamento';
  if (isCardOneTimeSelected.value) return `Você economiza R$ ${toBRL(checkoutSavingsValue.value)} pagando em 1x`;
  return `Você economiza R$ ${toBRL(checkoutSavingsValue.value)} hoje`;
});
const paymentSummaryText = computed(() => {
  const discount = Math.round(checkoutDiscountRate.value * 100);
  if (isPixSelected.value) return `de ${fullPriceLabel.value} por ${displayedCheckoutPrice.value} no PIX (${discount}% OFF).`;
  if (isCardOneTimeSelected.value && checkoutDisplayValue.value < checkoutReferenceValue.value) {
    return `${displayedCheckoutPrice.value} no cartão à vista, com desconto aplicado.`;
  }
  if (isCardOneTimeSelected.value) return `${displayedCheckoutPrice.value} no cartão à vista.`;
  if (form.value.billing_type === 'BOLETO') return `${displayedCheckoutPrice.value} no boleto, sem desconto adicional.`;
  return `${displayedCheckoutPrice.value} no cartão parcelado, no valor normal do plano.`;
});

const parseExpiry = (value) => {
  const clean = String(value || '').trim();
  const match = clean.match(/^(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  if (month < 1 || month > 12) return null;
  return { month: match[1], year: match[2] };
};

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const sanitizeCpfCnpjInput = () => {
  form.value.cpf_cnpj = onlyDigits(form.value.cpf_cnpj).slice(0, 14);
};

const sanitizeUserNameInput = () => {
  form.value.user_name = String(form.value.user_name || '').replace(/\s/g, '').slice(0, USERNAME_MAX_LENGTH);
};

const sanitizeCardNumberInput = () => {
  form.value.credit_card.number = onlyDigits(form.value.credit_card.number).slice(0, 19);
};

const sanitizeExpiryInput = () => {
  const digits = onlyDigits(form.value.credit_card.expiry).slice(0, 6);
  if (digits.length <= 2) {
    form.value.credit_card.expiry = digits;
    return;
  }
  form.value.credit_card.expiry = `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const sanitizeCvvInput = () => {
  form.value.credit_card.ccv = onlyDigits(form.value.credit_card.ccv).slice(0, 3);
};

const sanitizeCepInput = () => {
  form.value.credit_card.holder_info.postal_code = onlyDigits(form.value.credit_card.holder_info.postal_code).slice(0, 8);
};

const sanitizeAddressNumberInput = () => {
  form.value.credit_card.holder_info.address_number = onlyDigits(form.value.credit_card.holder_info.address_number).slice(0, 10);
};

const sanitizePhoneInput = () => {
  form.value.credit_card.holder_info.phone = onlyDigits(form.value.credit_card.holder_info.phone).slice(0, 11);
};

const syncCreditCardIdentity = () => {
  if (!isCreditCardSelected.value) return;
  if (!form.value.credit_card.holder_info.name) {
    form.value.credit_card.holder_info.name = form.value.user_name || '';
  }
  form.value.credit_card.holder_info.email = form.value.email || '';
  form.value.credit_card.holder_info.cpf_cnpj = form.value.cpf_cnpj || '';
};

const setupHeroObserver = () => {
  if (!heroAnchor.value) return;
  heroObserver = new IntersectionObserver(
    (entries) => {
      const [entry] = entries;
      showStickyCta.value = !entry.isIntersecting;
    },
    { threshold: 0.35 },
  );
  heroObserver.observe(heroAnchor.value);
};

const setupReveal = () => {
  const revealables = document.querySelectorAll('[data-reveal]');
  if (prefersReducedMotion) {
    revealables.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
  );

  revealables.forEach((el) => revealObserver.observe(el));
};

const validateForm = () => {
  formError.value = '';
  const payload = form.value;
  logCheckout('info', 'validate_start', {
    email: maskEmail(payload.email),
    billing_type: payload.billing_type,
  });

  if (!payload.cpf_cnpj) {
    logCheckout('warn', 'validate_failed', { reason: 'cpf_cnpj_required' });
    formError.value = 'CPF/CNPJ é obrigatório.';
    return false;
  }
  if (!payload.email) {
    logCheckout('warn', 'validate_failed', { reason: 'email_required' });
    formError.value = 'E-mail é obrigatório.';
    return false;
  }
  if (!payload.user_name || /\s/.test(payload.user_name)) {
    logCheckout('warn', 'validate_failed', { reason: 'username_invalid' });
    formError.value = 'User não pode conter espaços.';
    return false;
  }
  if (payload.user_name.length > USERNAME_MAX_LENGTH) {
    logCheckout('warn', 'validate_failed', { reason: 'username_too_long', len: payload.user_name.length });
    formError.value = `User deve ter no máximo ${USERNAME_MAX_LENGTH} caracteres.`;
    return false;
  }
  if (!payload.password || payload.password.length < 8) {
    logCheckout('warn', 'validate_failed', { reason: 'password_too_short' });
    formError.value = 'Senha precisa ter no mínimo 8 caracteres.';
    return false;
  }
  if (!['PIX', 'BOLETO', 'UNDEFINED', 'CREDIT_CARD'].includes(payload.billing_type)) {
    logCheckout('warn', 'validate_failed', { reason: 'billing_type_invalid', billing_type: payload.billing_type });
    formError.value = 'Forma de pagamento inválida.';
    return false;
  }
  if (payload.billing_type === 'CREDIT_CARD') {
    const card = payload.credit_card || {};
    const holder = card.holder_info || {};
    const expiry = parseExpiry(card.expiry);

    if (!Number.isInteger(payload.installment_count) || payload.installment_count < 1 || payload.installment_count > 12) {
      logCheckout('warn', 'validate_failed', { reason: 'installments_invalid', installment_count: payload.installment_count });
      formError.value = 'Parcelas devem estar entre 1 e 12.';
      return false;
    }
    if (!card.number || !card.ccv) {
      logCheckout('warn', 'validate_failed', { reason: 'card_data_missing' });
      formError.value = 'Preencha todos os dados do cartão.';
      return false;
    }
    if (!expiry) {
      logCheckout('warn', 'validate_failed', { reason: 'card_expiry_invalid' });
      formError.value = 'Validade inválida. Use MM/AAAA.';
      return false;
    }
    if (
      !holder.name ||
      !holder.phone ||
      !holder.postal_code ||
      !holder.address_number
    ) {
      formError.value = 'Preencha todos os dados do titular do cartao.';
      logCheckout('warn', 'validate_failed', { reason: 'card_holder_data_missing' });
      return false;
    }
  }
  logCheckout('info', 'validate_ok', {
    email: maskEmail(payload.email),
    billing_type: payload.billing_type,
  });
  return true;
};

const getCheckoutErrorMessage = (error, billingType) => {
  const rawMessage = String(error?.response?.data?.error || error?.message || '').trim();
  const normalized = rawMessage.toLowerCase();

  if (billingType === 'CREDIT_CARD') {
    if (normalized.includes('ccv') || normalized.includes('cvv')) {
      return 'CVV invalido. Revise o codigo de seguranca do cartao.';
    }
    if (
      normalized.includes('numero do cartao')
      || normalized.includes('cartao invalido')
      || normalized.includes('card number')
      || normalized.includes('invalid card')
    ) {
      return 'Numero do cartao invalido. Revise os dados e tente novamente.';
    }
    if (normalized.includes('expir') || normalized.includes('validade')) {
      return 'Cartao com validade invalida ou expirada. Confira mes e ano.';
    }
    if (
      normalized.includes('limite')
      || normalized.includes('insufficient')
      || normalized.includes('saldo')
      || normalized.includes('funds')
    ) {
      return 'Pagamento recusado por limite/saldo insuficiente. Tente outro cartao ou PIX.';
    }
    if (
      normalized.includes('nao autorizado')
      || normalized.includes('not authorized')
      || normalized.includes('denied')
      || normalized.includes('recusad')
      || normalized.includes('rejected')
    ) {
      return 'Pagamento no cartao foi recusado pela operadora. Tente outro cartao ou PIX.';
    }
    if (
      normalized.includes('titular')
      || normalized.includes('holder')
      || normalized.includes('cpf')
      || normalized.includes('postal')
      || normalized.includes('address')
    ) {
      return 'Dados do titular do cartao invalidos. Revise CPF, CEP, numero e telefone.';
    }
    return 'Nao foi possivel aprovar o pagamento no cartao. Revise os dados e tente novamente.';
  }

  return rawMessage || 'Nao foi possivel iniciar o pagamento.';
};

const stopPaymentPolling = () => {
  if (paymentPollTimer) {
    window.clearInterval(paymentPollTimer);
    paymentPollTimer = null;
  }
};

const stopLoginRedirect = () => {
  if (loginRedirectTimer) {
    window.clearTimeout(loginRedirectTimer);
    loginRedirectTimer = null;
  }
};

const isPaidStatus = (status) => PAID_STATUSES.includes(String(status || '').toUpperCase());

const handlePaymentConfirmed = (status) => {
  const normalizedStatus = String(status || '').toUpperCase();
  liveStatus.value = normalizedStatus;
  checkoutSuccessVisible.value = true;
  checkoutSuccessMessage.value = 'Pagamento confirmado! Redirecionando para o login...';
  closePaymentPopup();
  stopPaymentPolling();
  stopLoginRedirect();
  loginRedirectTimer = window.setTimeout(() => {
    closeCheckoutModal();
    router.push({ name: 'Login' });
  }, 2500);
};

const pollPaymentStatus = (paymentId) => {
  if (!paymentId) {
    logCheckout('warn', 'poll_skip_empty_payment_id');
    return;
  }
  logCheckout('info', 'poll_start', { payment_id: paymentId });
  stopPaymentPolling();

  paymentPollTimer = window.setInterval(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/auth/payment-status/${paymentId}`);
      const statusData = response?.data || {};
      const status = String(statusData.status || '').toUpperCase();
      liveStatus.value = status;
      if (payment.value) {
        payment.value = {
          ...payment.value,
          payment_status: status || payment.value.payment_status,
          billing_type: statusData.billing_type || payment.value.billing_type,
          invoice_url: statusData.invoice_url || payment.value.invoice_url,
          bank_slip_url: statusData.bank_slip_url || payment.value.bank_slip_url,
          pix_qr_code: statusData.pix_qr_code || payment.value.pix_qr_code,
          pix_copy_paste: statusData.pix_copy_paste || payment.value.pix_copy_paste,
        };
      }
      logCheckout('info', 'poll_tick', {
        payment_id: paymentId,
        status,
        billing_type: statusData.billing_type,
        has_pix_qr: Boolean(statusData.pix_qr_code || payment.value?.pix_qr_code),
        has_pix_copy: Boolean(statusData.pix_copy_paste || payment.value?.pix_copy_paste),
      });

      if (isPaidStatus(status)) {
        handlePaymentConfirmed(status);
      }
    } catch (error) {
      logCheckout('warn', 'poll_error', {
        payment_id: paymentId,
        error: error?.response?.data?.error || error?.message || 'unknown',
      });
    }
  }, 8000);
};

const submitCheckout = async () => {
  if (!validateForm()) return;
  isSubmitting.value = true;
  formError.value = '';
  checkoutSuccessMessage.value = '';
  checkoutSuccessVisible.value = false;
  payment.value = null;
  liveStatus.value = '';
  pixCopied.value = false;
  lastSubmittedBillingType.value = form.value.billing_type;
  logCheckout('info', 'submit_start', {
    billing_type: form.value.billing_type,
    email: maskEmail(form.value.email),
    plan_id: Number(form.value.plan_id) || 1,
    checkout_offer: selectedCheckoutPlanKey.value,
  });

  try {
    const payload = {
      email: form.value.email,
      password: form.value.password,
      user_name: form.value.user_name,
      cpf_cnpj: form.value.cpf_cnpj,
      plan_id: Number(form.value.plan_id) || 1,
      billing_type: form.value.billing_type,
      checkout_offer: selectedCheckoutPlanKey.value,
    };
    if (form.value.billing_type === 'CREDIT_CARD') {
      const expiry = parseExpiry(form.value.credit_card.expiry);
      if (!expiry) {
        logCheckout('warn', 'submit_credit_card_invalid_expiry');
        formError.value = 'Validade inválida. Use MM/AAAA.';
        isSubmitting.value = false;
        return;
      }
      payload.installment_count = Number(form.value.installment_count) || 1;
      payload.credit_card = {
        holder_name: form.value.credit_card.holder_info.name,
        number: form.value.credit_card.number,
        expiry_month: expiry.month,
        expiry_year: expiry.year,
        ccv: form.value.credit_card.ccv,
        holder_info: {
          name: form.value.credit_card.holder_info.name,
          email: form.value.email,
          cpf_cnpj: form.value.cpf_cnpj,
          postal_code: form.value.credit_card.holder_info.postal_code,
          address_number: form.value.credit_card.holder_info.address_number,
          phone: form.value.credit_card.holder_info.phone,
        },
      };
    }

    const response = await axios.post(`${API_BASE_URL}/api/v1/auth/signup`, payload);
    payment.value = response?.data || null;
    liveStatus.value = String(response?.data?.payment_status || '').toUpperCase();
    openPaymentPopup();
    logCheckout('info', 'submit_success', {
      payment_id: response?.data?.payment_id,
      status: liveStatus.value,
      billing_type: response?.data?.billing_type,
      has_pix_qr: Boolean(response?.data?.pix_qr_code),
      has_pix_copy: Boolean(response?.data?.pix_copy_paste),
    });
    if (isPaidStatus(liveStatus.value)) {
      handlePaymentConfirmed(liveStatus.value);
      return;
    }
    if (payment.value?.payment_id) {
      pollPaymentStatus(payment.value.payment_id);
    }
  } catch (error) {
    logCheckout('error', 'submit_error', {
      status_code: error?.response?.status,
      data: error?.response?.data,
      message: error?.message,
    });
    formError.value = getCheckoutErrorMessage(error, form.value.billing_type);
  } finally {
    isSubmitting.value = false;
    logCheckout('info', 'submit_finished', { is_submitting: false });
  }
};

const scrollToOferta = () => {
  const target = document.getElementById('oferta');
  if (!target) return;
  target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
};

const openCheckoutModal = (source = 'unknown', planKey = 'mensal') => {
  if (planKey && CHECKOUT_PLANS[planKey]) {
    selectedCheckoutPlanKey.value = planKey;
    form.value.plan_id = CHECKOUT_PLANS[planKey].planId;
  }
  logCheckout('info', 'modal_open_request', {
    source,
    plan_key: selectedCheckoutPlanKey.value,
    was_open: isCheckoutOpen.value,
  });
  isCheckoutOpen.value = true;
};

const openPaymentPopup = () => {
  if (!payment.value) return;
  isPaymentPopupOpen.value = true;
  logCheckout('info', 'payment_popup_open', {
    payment_id: payment.value?.payment_id,
    has_pix_qr: Boolean(payment.value?.pix_qr_code),
    has_pix_copy: Boolean(payment.value?.pix_copy_paste),
  });
};

const closePaymentPopup = () => {
  isPaymentPopupOpen.value = false;
  logCheckout('info', 'payment_popup_close', {
    payment_id: payment.value?.payment_id,
  });
};

const closeCheckoutModal = () => {
  logCheckout('info', 'modal_close', {
    live_status: liveStatus.value,
    payment_id: payment.value?.payment_id,
  });
  isCheckoutOpen.value = false;
  checkoutSuccessVisible.value = false;
  checkoutSuccessMessage.value = '';
  lastSubmittedBillingType.value = '';
  closePaymentPopup();
  stopPaymentPolling();
  stopLoginRedirect();
};

const copyPix = async () => {
  if (!payment.value?.pix_copy_paste) return;
  try {
    await navigator.clipboard.writeText(payment.value.pix_copy_paste);
    pixCopied.value = true;
    logCheckout('info', 'pix_copy_success', { payment_id: payment.value?.payment_id });
    window.setTimeout(() => {
      pixCopied.value = false;
    }, 1800);
  } catch (error) {
    pixCopied.value = false;
    logCheckout('warn', 'pix_copy_failed', {
      payment_id: payment.value?.payment_id,
      error: error?.message || 'unknown',
    });
  }
};

function toBRL(value) {
  const numeric = Number(value) || 0;
  return numeric.toFixed(2).replace('.', ',');
}

const formatDate = (value) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString('pt-BR');
};

watch([isCheckoutOpen, isPaymentPopupOpen], ([checkoutOpen, paymentPopupOpen]) => {
  const shouldLockBody = checkoutOpen || paymentPopupOpen;
  document.body.style.overflow = shouldLockBody ? 'hidden' : '';
  logCheckout('info', 'modal_state_changed', {
    checkout_open: checkoutOpen,
    payment_popup_open: paymentPopupOpen,
    body_locked: shouldLockBody,
  });
});

watch(isCreditCardSelected, (selected) => {
  if (selected) {
    syncCreditCardIdentity();
    return;
  }
  form.value.installment_count = 1;
  form.value.credit_card = {
    holder_name: '',
    number: '',
    expiry: '',
    ccv: '',
    holder_info: {
      name: '',
      email: '',
      cpf_cnpj: '',
      postal_code: '',
      address_number: '',
      phone: '',
    },
  };
});

watch(
  () => [form.value.user_name, form.value.email, form.value.cpf_cnpj],
  () => {
    syncCreditCardIdentity();
  },
);

onMounted(async () => {
  await nextTick();
  setupHeroObserver();
  setupReveal();

  // Deep-link de plano (ex.: ?plano=semestral vindo do Discord) abre o checkout direto
  const params = new URLSearchParams(window.location.search);
  const planoParam = String(params.get('plano') || params.get('plan') || '').toLowerCase();
  if (CHECKOUT_PLANS[planoParam]) {
    openCheckoutModal('deep-link', planoParam);
  }
});

onBeforeUnmount(() => {
  document.body.style.overflow = '';
  stopPaymentPolling();
  stopLoginRedirect();
  if (heroObserver) heroObserver.disconnect();
  if (revealObserver) revealObserver.disconnect();
});
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;600&display=swap');

:root {
  --landing-bg: #11131a;
  --landing-surface: #0b0d14;
  --landing-surface-soft: #141a26;
  --landing-foreground: #f3f6ff;
  --landing-muted: rgba(226, 232, 255, 0.75);
  --landing-subtle: rgba(169, 183, 220, 0.75);
  --landing-accent: #4e73df;
  --landing-accent-2: #9b59b6;
  --landing-positive: #38d39f;
  --landing-danger: #ff7c8f;
  --landing-border: rgba(115, 148, 240, 0.22);
  --landing-font-display: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
  --landing-font-mono: 'IBM Plex Mono', 'Space Mono', monospace;
  --zima-bg: var(--landing-bg);
  --zima-surface: var(--landing-surface);
  --zima-surface-strong: var(--landing-surface-soft);
  --zima-foreground: var(--landing-foreground);
  --zima-muted: var(--landing-muted);
  --zima-subtle: var(--landing-subtle);
  --zima-accent: var(--landing-accent);
  --zima-electric: var(--landing-accent-2);
  --zima-lines: rgba(255, 255, 255, 0.04);
  --zima-grid-minor: rgba(115, 148, 240, 0.08);
  --zima-grid-major: rgba(115, 148, 240, 0.16);
  --zima-glow: rgba(115, 148, 240, 0.4);
  --zima-font-display: var(--landing-font-display);
  --zima-font-mono: var(--landing-font-mono);
}

body {
  margin: 0;
  background: #181a20;
  color: var(--landing-foreground);
  font-family: var(--landing-font-display);
  -webkit-font-smoothing: antialiased;
}

/* Marca ZIMA — global para alcançar conteúdo renderizado via v-html (o scoped CSS não alcança) */
.word-brand {
  background: linear-gradient(120deg, #6fa6ff 0%, #bd8bff 45%, #7ea5ff 75%, #d2b2ff 100%);
  background-size: 220% 220%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 14px rgba(127, 166, 255, 0.28);
  animation: zima-brand-shift-global 4.2s ease-in-out infinite;
  will-change: background-position;
}

@keyframes zima-brand-shift-global {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@media (prefers-reduced-motion: reduce) {
  .word-brand {
    animation: none !important;
  }
}

.landing-page {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  background:
    radial-gradient(circle at top left, rgba(78, 115, 223, 0.16), transparent 45%),
    radial-gradient(circle at top right, rgba(155, 89, 182, 0.16), transparent 42%),
    var(--landing-bg);
  isolation: isolate;
}

.bg-grid,
.bg-glow {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.bg-grid {
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.36;
}

.bg-glow {
  background:
    radial-gradient(circle at 15% 25%, rgba(78, 115, 223, 0.12), transparent 38%),
    radial-gradient(circle at 85% 8%, rgba(155, 89, 182, 0.12), transparent 35%);
}

.layout {
  width: min(1160px, 92vw);
  margin: 0 auto;
  padding: 38px 0 32px;
  display: grid;
  gap: clamp(44px, 6vw, 76px);
  position: relative;
  z-index: 1;
}

.layout > * {
  scroll-snap-align: start;
  scroll-margin-top: 60px;
}

.scroll-progress {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.05);
  z-index: 20;
}

.scroll-progress .bar {
  height: 100%;
  background: linear-gradient(90deg, var(--landing-accent), var(--landing-accent-2));
  box-shadow: 0 0 16px rgba(115, 148, 240, 0.5);
  transition: width 0.12s ease;
}

.section-shell {
  position: relative;
  padding: clamp(24px, 3.6vw, 34px);
  border-radius: 20px;
  background: rgba(11, 13, 20, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.36);
  overflow: hidden;
}

.section-shell::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  background: linear-gradient(120deg, var(--landing-accent), var(--landing-accent-2));
  border-radius: 999px;
  opacity: 0.9;
}

.section-header {
  display: grid;
  gap: 4px;
}

.eyebrow {
  font-family: var(--landing-font-mono);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--landing-subtle);
  font-size: 12px;
}

.cta {
  cursor: pointer;
  font-family: var(--landing-font-display);
  color: var(--landing-foreground);
}

.cta.primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  padding: 14px 20px;
  border-radius: 999px;
  border: 1px solid rgba(115, 148, 240, 0.55);
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.92), rgba(155, 89, 182, 0.92), rgba(78, 115, 223, 0.92));
  color: var(--zima-foreground);
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow:
    0 0 0 1px rgba(115, 148, 240, 0.28) inset,
    0 18px 48px rgba(20, 40, 80, 0.6),
    0 0 24px rgba(115, 148, 240, 0.34);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.cta.primary:hover {
  box-shadow:
    0 0 0 1px rgba(170, 191, 255, 0.52) inset,
    0 20px 50px rgba(10, 20, 40, 0.72),
    0 0 26px rgba(155, 89, 182, 0.35);
}

.cta.primary:active {
  transform: translateY(1px);
}

.landing-footer {
  width: min(1160px, 92vw);
  margin: 0 auto 24px;
  color: var(--landing-subtle);
  font-size: 12px;
  text-align: center;
  position: relative;
  z-index: 1;
}

.sticky-cta {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 12px;
  padding: 10px 14px;
  display: flex;
  justify-content: center;
  z-index: 22;
}

.sticky-cta .cta {
  width: min(480px, 94vw);
  padding: 14px 18px;
}

.disclaimer {
  margin: 0;
}

.guarantee {
  margin: 6px 0 0;
  color: var(--landing-muted);
  font-size: 12px;
}

[data-reveal] {
  opacity: 0;
  transform: translateY(18px) scale(0.98);
  transition: opacity 0.5s ease, transform 0.6s ease;
}

[data-reveal].is-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.reduce-motion * {
  animation: none !important;
  transition-duration: 0ms !important;
}

.checkout-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background:
    radial-gradient(circle at 16% 24%, rgba(78, 115, 223, 0.14), transparent 44%),
    radial-gradient(circle at 84% 8%, rgba(155, 89, 182, 0.16), transparent 40%),
    rgba(4, 7, 14, 0.76);
  backdrop-filter: blur(9px) saturate(115%);
  display: grid;
  place-items: center;
  padding: 18px;
  overflow: hidden;
}

.payment-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(4, 7, 14, 0.82);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  padding: 18px;
}

.payment-popup {
  width: min(520px, 100%);
  border-radius: 18px;
  border: 1px solid rgba(140, 165, 242, 0.35);
  background:
    linear-gradient(160deg, rgba(14, 20, 34, 0.95), rgba(10, 14, 26, 0.95)) padding-box,
    linear-gradient(125deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.88)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 24px 70px rgba(0, 0, 0, 0.56),
    0 0 28px rgba(155, 89, 182, 0.18);
  padding: 22px;
  position: relative;
  display: grid;
  gap: 12px;
}

.payment-popup h3 {
  margin: 0 28px 2px 0;
  color: #fff;
  font-size: 24px;
  line-height: 1.1;
}

.checkout-modal {
  width: min(960px, 100%);
  max-height: 94vh;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid transparent;
  background:
    linear-gradient(160deg, rgba(14, 20, 34, 0.92), rgba(10, 14, 26, 0.9)) padding-box,
    linear-gradient(125deg, rgba(78, 115, 223, 0.88), rgba(155, 89, 182, 0.88)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 0 1px rgba(78, 115, 223, 0.24),
    0 24px 70px rgba(0, 0, 0, 0.56),
    0 0 28px rgba(155, 89, 182, 0.18);
  padding: 26px;
  position: relative;
  overscroll-behavior: contain;
}

.modal-close {
  position: absolute;
  top: 13px;
  right: 13px;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, color 0.2s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.42);
  color: #fff;
  transform: translateY(-1px);
}

.modal-close span {
  font-size: 18px;
  line-height: 1;
}

.checkout-head {
  margin-bottom: 18px;
}

.kicker {
  margin: 0;
  color: var(--landing-subtle);
  font-family: var(--landing-font-mono);
  font-size: 12px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.checkout-head h2 {
  margin: 4px 0 10px;
  font-size: clamp(24px, 3.3vw, 34px);
  color: #fff;
  line-height: 1.07;
}

.checkout-head .sub {
  margin: 0;
  color: rgba(226, 232, 255, 0.82);
  max-width: 62ch;
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 18px;
  max-height: calc(94vh - 136px);
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(152, 178, 255, 0.72) rgba(255, 255, 255, 0.08);
}

.checkout-grid::-webkit-scrollbar {
  width: 8px;
}

.checkout-grid::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
}

.checkout-grid::-webkit-scrollbar-thumb {
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: padding-box;
  background:
    linear-gradient(180deg, rgba(133, 170, 255, 0.9), rgba(186, 132, 241, 0.88)) padding-box;
}

.checkout-grid::-webkit-scrollbar-thumb:hover {
  background:
    linear-gradient(180deg, rgba(160, 190, 255, 0.96), rgba(201, 148, 246, 0.94)) padding-box;
}

.checkout-form {
  display: grid;
  gap: 14px;
  background: linear-gradient(180deg, rgba(12, 18, 34, 0.78), rgba(10, 15, 29, 0.74));
  border: 1px solid rgba(136, 162, 238, 0.24);
  border-radius: 18px;
  padding: 18px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 10px 28px rgba(5, 10, 22, 0.35);
}

.checkout-form label {
  display: grid;
  gap: 7px;
  position: relative;
}

.checkout-form span {
  font-size: 11px;
  color: rgba(185, 200, 236, 0.88);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
}

.checkout-form input,
.checkout-form select {
  width: 100%;
  box-sizing: border-box;
  height: 48px;
  border: 1px solid rgba(154, 177, 245, 0.28);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.07));
  color: #fff;
  padding: 0 15px;
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.checkout-form select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 42px;
  background-image:
    linear-gradient(45deg, transparent 50%, #d9e4ff 50%),
    linear-gradient(135deg, #d9e4ff 50%, transparent 50%);
  background-position:
    calc(100% - 18px) calc(50% - 2px),
    calc(100% - 12px) calc(50% - 2px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
}

.checkout-form select option {
  background: #141d33;
  color: #edf3ff;
}

.checkout-form select option:checked {
  background: #2a61c6;
  color: #ffffff;
}

.checkout-form input::placeholder {
  color: rgba(226, 233, 251, 0.52);
}

.checkout-form input:focus,
.checkout-form select:focus {
  border-color: rgba(182, 201, 255, 0.68);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.08));
  box-shadow: 0 0 0 3px rgba(98, 138, 240, 0.2);
}

.checkout-form input:-webkit-autofill,
.checkout-form input:-webkit-autofill:hover,
.checkout-form input:-webkit-autofill:focus,
.checkout-form input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.1) inset !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  -webkit-text-fill-color: #ffffff !important;
  transition: background-color 5000s ease-in-out 0s;
}

.password-field input {
  padding-right: 44px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  bottom: 10px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: rgba(235, 241, 255, 0.74);
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 14px;
  transition: color 0.2s ease, transform 0.15s ease;
}

.password-toggle:hover {
  color: #ffffff;
  transform: translateY(-1px);
}

.payment-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.payment-tab {
  min-height: 42px;
  border-radius: 11px;
  border: 1px solid rgba(140, 165, 242, 0.26);
  background: rgba(255, 255, 255, 0.1);
  color: rgba(233, 240, 255, 0.92);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
}

.payment-tab:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(173, 194, 255, 0.5);
}

.payment-tab.active {
  color: #f9fcff;
  border-color: rgba(154, 184, 255, 0.78);
  background:
    linear-gradient(130deg, rgba(78, 115, 223, 0.64), rgba(155, 89, 182, 0.58)) padding-box,
    linear-gradient(130deg, rgba(188, 212, 255, 0.95), rgba(211, 176, 244, 0.9)) border-box;
  box-shadow: 0 8px 20px rgba(77, 111, 219, 0.28);
}

.installments-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
}

.installment-btn {
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(140, 165, 242, 0.26);
  background: rgba(255, 255, 255, 0.1);
  color: rgba(232, 239, 255, 0.9);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
}

.installment-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(173, 194, 255, 0.5);
}

.installment-btn.active {
  color: #ffffff;
  border-color: rgba(154, 184, 255, 0.78);
  background:
    linear-gradient(130deg, rgba(78, 115, 223, 0.64), rgba(155, 89, 182, 0.58)) padding-box,
    linear-gradient(130deg, rgba(188, 212, 255, 0.95), rgba(211, 176, 244, 0.9)) border-box;
}

.credit-fields {
  display: grid;
  gap: 12px;
}

.card-row {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.card-row > label {
  min-width: 0;
}

.actions {
  display: grid;
  gap: 10px;
  margin-top: 8px;
}

.cta.full {
  width: 100%;
  min-height: 44px;
}

.ghost {
  height: 42px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.ghost:hover {
  background: rgba(255, 255, 255, 0.13);
  border-color: rgba(255, 255, 255, 0.42);
  transform: translateY(-1px);
}

.ghost.mini {
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
}

.checkout-side {
  display: grid;
  gap: 14px;
  align-content: start;
}

.summary-card,
.payment-card {
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(140, 165, 242, 0.24);
  background:
    linear-gradient(145deg, rgba(19, 26, 44, 0.72), rgba(12, 17, 32, 0.78)) padding-box,
    linear-gradient(130deg, rgba(78, 115, 223, 0.36), rgba(155, 89, 182, 0.28)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 14px 32px rgba(0, 0, 0, 0.26);
}

.summary-card {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 82% 18%, rgba(145, 111, 220, 0.22), transparent 48%),
    radial-gradient(circle at 12% 100%, rgba(78, 115, 223, 0.16), transparent 48%),
    linear-gradient(150deg, rgba(20, 30, 52, 0.78), rgba(13, 18, 34, 0.82)) padding-box,
    linear-gradient(130deg, rgba(98, 139, 246, 0.48), rgba(173, 112, 226, 0.34)) border-box;
}

.summary-card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 2px;
  background: linear-gradient(90deg, rgba(125, 167, 255, 0), rgba(130, 172, 255, 0.92), rgba(185, 131, 239, 0.85), rgba(125, 167, 255, 0));
  opacity: 0.95;
}

.summary-label {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(213, 224, 255, 0.74);
}

.summary-card h3 {
  margin: 0;
  color: #f4f8ff;
  font-size: 35px;
  line-height: 1.03;
  letter-spacing: -0.01em;
}

.summary-price {
  margin: 10px 0 3px;
  color: #9be4ca;
  font-size: 46px;
  font-weight: 800;
  line-height: 1;
  text-shadow: 0 0 22px rgba(56, 211, 159, 0.2);
}

.summary-price-old {
  margin: 8px 0 2px;
  color: rgba(226, 232, 255, 0.62);
  font-size: 16px;
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}

.summary-wow-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 10px;
  padding: 0;
  border: 0;
  border-radius: 0;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #c7f5e5;
  background: transparent;
  text-shadow: 0 0 14px rgba(56, 211, 159, 0.18);
}

.summary-wow-badge::before {
  content: '';
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: radial-gradient(circle, #9df1cf 0%, #38d39f 62%, rgba(56, 211, 159, 0.15) 100%);
  box-shadow:
    0 0 0 3px rgba(56, 211, 159, 0.14),
    0 0 16px rgba(56, 211, 159, 0.45);
  animation: wowPulse 2.1s ease-in-out infinite;
}

.reduce-motion .summary-wow-badge::before {
  animation: none !important;
}

@keyframes wowPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow:
      0 0 0 3px rgba(56, 211, 159, 0.12),
      0 0 14px rgba(56, 211, 159, 0.34);
  }
  50% {
    transform: scale(1.15);
    box-shadow:
      0 0 0 5px rgba(56, 211, 159, 0.18),
      0 0 22px rgba(56, 211, 159, 0.48);
  }
}

.summary-meta {
  margin: 4px 0 0;
  color: rgba(226, 232, 255, 0.74);
  font-size: 13px;
}

.summary-discount {
  margin-top: 12px;
}

.summary-saving {
  margin: 10px 0 0;
  color: #9be4ca;
  font-size: 13px;
  font-weight: 700;
}

.payment-card p {
  margin: 6px 0;
  color: rgba(226, 232, 255, 0.76);
}

.payment-cta-card {
  display: grid;
  gap: 10px;
}

.payment-links {
  display: grid;
  gap: 6px;
  margin-top: 10px;
}

.payment-links a {
  color: #d3ddff;
  text-decoration: none;
  font-weight: 600;
}

.payment-links a:hover {
  color: #eef4ff;
  text-decoration: underline;
}

.pix-box {
  margin-top: 10px;
  border: 1px solid rgba(150, 174, 246, 0.28);
  border-radius: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
}

.pix-qr-box {
  margin-top: 10px;
  border: 1px solid rgba(150, 174, 246, 0.28);
  border-radius: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  display: grid;
  gap: 8px;
  justify-items: center;
  text-align: center;
}

.pix-qr-image {
  width: min(240px, 100%);
  aspect-ratio: 1 / 1;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: #ffffff;
  padding: 8px;
  margin: 0 auto;
}

.pix-label {
  margin: 0 0 6px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 12px;
}

.pix-box code {
  display: block;
  word-break: break-all;
  color: #e5ecff;
  font-size: 12px;
  margin-bottom: 8px;
}

.form-error {
  margin: 4px 0 2px;
  color: #ff9aae;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid rgba(255, 120, 146, 0.34);
  border-radius: 10px;
  padding: 8px 10px;
  background: rgba(255, 124, 143, 0.12);
}

.form-success {
  margin: 4px 0 2px;
  color: #b8f5df;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid rgba(82, 212, 162, 0.38);
  border-radius: 10px;
  padding: 8px 10px;
  background: rgba(44, 170, 123, 0.16);
}

.success-preview-card {
  border-color: rgba(82, 212, 162, 0.35);
  background:
    radial-gradient(circle at 84% 18%, rgba(57, 203, 151, 0.16), transparent 48%),
    linear-gradient(145deg, rgba(15, 30, 31, 0.74), rgba(10, 25, 29, 0.78)) padding-box,
    linear-gradient(130deg, rgba(79, 198, 155, 0.4), rgba(87, 149, 238, 0.24)) border-box;
}

.success-preview-title {
  margin: 0;
  color: #b8f5df;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.08;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (min-width: 768px) {
  .sticky-cta {
    display: none;
  }
}

@media (max-width: 900px) {
  .checkout-grid {
    grid-template-columns: 1fr;
    max-height: calc(94vh - 128px);
    padding-right: 4px;
  }
}

@media (max-width: 767px) {
  .layout {
    padding: 26px 0 84px;
    gap: 16px;
  }

  .section-shell {
    padding: 20px;
    border-radius: 16px;
  }

  .checkout-modal {
    padding: 18px;
    border-radius: 16px;
  }

  .checkout-form {
    padding: 14px;
  }

  .checkout-grid {
    max-height: calc(94vh - 122px);
    padding-right: 0;
  }

  .checkout-side {
    order: -1;
  }

  .card-row {
    grid-template-columns: 1fr;
  }

  .payment-tabs {
    grid-template-columns: 1fr;
  }

  .installments-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .summary-card h3 {
    font-size: 30px;
  }

  .summary-price {
    font-size: 40px;
  }
}
</style>

